// src/cart/CartView.js
import React, { useState, useEffect, useCallback } from 'react';
import CartItem from './CartItem';
import { callApi } from '../api/api';

const INDIAN_STATES = [
    'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana',
    'Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur',
    'Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana',
    'Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu & Kashmir','Ladakh',
    'Puducherry','Chandigarh','Andaman & Nicobar','Dadra & NH','Lakshadweep'
];

const CartView = ({ cart, setCart, isAuthenticated, currentUserId, currentUserName, currentUserMobile, currentUserAddress, currentUserAddresses, currentUserWalletBalance, showPage, setOrderForPayment }) => {
    const [checkoutMessage, setCheckoutMessage] = useState('');
    const [messageType, setMessageType] = useState('error');
    const [deliveryMethod, setDeliveryMethod] = useState('Home Delivery');
    const [isLoading, setIsLoading] = useState(false);
    const [useWallet, setUseWallet] = useState(true);
    const [referredBy, setReferredBy] = useState('');
    const [patientAge, setPatientAge] = useState('');
    const [patientGender, setPatientGender] = useState('');

    // Address fields
    const [address, setAddress] = useState({
        name: '', phone: '', line1: '', line2: '',
        pincode: '', city: '', state: ''
    });

    useEffect(() => {
        if (currentUserAddresses && currentUserAddresses.length > 0) {
            const defaultAddr = currentUserAddresses.find(a => a.isDefault || a.label === 'Home') || currentUserAddresses[0];
            setAddress({
                ...defaultAddr,
                name: defaultAddr.name || currentUserName || '',
                phone: defaultAddr.phone || currentUserMobile || ''
            });
        } else if (currentUserAddress) {
            setAddress(prev => ({
                ...prev,
                line1: currentUserAddress,
                name: prev.name || currentUserName || '',
                phone: prev.phone || currentUserMobile || ''
            }));
        } else {
            setAddress(prev => ({
                ...prev,
                name: prev.name || currentUserName || '',
                phone: prev.phone || currentUserMobile || ''
            }));
        }
    }, [currentUserAddress, currentUserAddresses, currentUserName, currentUserMobile]);

    const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const finalTotalBeforeWallet = cartTotal;
    // Wallet usage is restricted to 10% of the cart value
    const maxWalletAllowed = cartTotal * 0.10;
    const walletAmountUsed = useWallet && isAuthenticated ? parseFloat(Math.min(currentUserWalletBalance || 0, maxWalletAllowed).toFixed(2)) : 0;
    const finalTotal = Math.max(0, finalTotalBeforeWallet - walletAmountUsed);

    const updateCartQuantity = useCallback((productId, action) => {
        setCart(prev =>
            prev.map(item => item.productId === productId
                ? { ...item, quantity: action === 'increase' ? item.quantity + 1 : item.quantity - 1 }
                : item
            ).filter(item => item.quantity > 0)
        );
    }, [setCart]);

    const removeFromCart = useCallback((productId) => {
        setCart(prev => prev.filter(item => item.productId !== productId));
    }, [setCart]);



    const handleCheckout = async () => {
        if (cart.length === 0) { setCheckoutMessage('Your cart is empty.'); setMessageType('error'); return; }
        if (!isAuthenticated) { setCheckoutMessage('Please log in to place your order.'); setMessageType('error'); setTimeout(() => showPage('login'), 2000); return; }

        if (deliveryMethod === 'Home Delivery') {
            if (!address.name.trim() || !address.phone.trim() || !address.line1.trim() || !address.pincode.trim() || !address.city.trim() || !address.state) {
                setCheckoutMessage('Please fill in all required delivery address fields.'); setMessageType('error'); return;
            }
            if (!/^\d{6}$/.test(address.pincode)) {
                setCheckoutMessage('Please enter a valid 6-digit pincode.'); setMessageType('error'); return;
            }
            const cleanPhone = address.phone.replace(/[\s\+]/g, '');
            const phoneToCheck = cleanPhone.startsWith('91') && cleanPhone.length > 10 ? cleanPhone.slice(2) : cleanPhone;
            if (!/^[6-9]\d{9}$/.test(phoneToCheck)) {
                setCheckoutMessage('Please enter a valid 10-digit mobile number.'); setMessageType('error'); return;
            }
        }

        const fullAddress = deliveryMethod === 'Home Delivery'
            ? `${address.name}, ${address.phone}, ${address.line1}${address.line2 ? ', ' + address.line2 : ''}, ${address.city}, ${address.state} - ${address.pincode}`
            : 'Store Pickup';

        const volumes = cart.filter(item => item.packageVolume).map(item => `${item.quantity}x ${item.packageVolume}`);
        const totalPackageVolume = volumes.length > 0 ? volumes.join(', ') : '';

        const orderData = {
            total: parseFloat(finalTotal.toFixed(2)),
            subTotal: parseFloat(cartTotal.toFixed(2)),
            items: cart.map(item => ({ productId: item.productId || item._id, name: item.name, price: item.price, quantity: item.quantity, isCustom: item.isCustom })),
            userId: currentUserId,
            deliveryMethod,
            deliveryAddress: fullAddress,
            customerName: address.name,
            customerPhone: address.phone ? (address.phone.startsWith('+91') ? address.phone : `+91${address.phone}`) : '',
            discountApplied: 0,
            walletAmountUsed: parseFloat(walletAmountUsed.toFixed(2)),
            packageVolume: totalPackageVolume,
            referredBy: referredBy || undefined,
            patientAge: patientAge ? parseInt(patientAge) : undefined,
            patientGender: patientGender || undefined
        };

        setIsLoading(true);
        try {
            const response = await callApi('/orders', 'POST', orderData);
            if (response.success) {
                setCart([]);
                setMessageType('success');
                if (response.order.status === 'Awaiting Bill') {
                    setCheckoutMessage(`Order #${response.order.orderId} placed! We will confirm the prices for your custom medicines and send a payment link.`);
                    setTimeout(() => showPage('orders'), 3000);
                } else {
                    setOrderForPayment({ orderId: response.order.orderId, total: response.order.total });
                    setCheckoutMessage(`Order #${response.order.orderId} placed! Redirecting to payment...`);
                    setTimeout(() => showPage('payment'), 1200);
                }
            } else {
                setMessageType('error');
                setCheckoutMessage(response.message || 'Failed to place order. Please try again.');
            }
        } catch (error) {
            setMessageType('error');
            setCheckoutMessage(error.message || 'An error occurred during checkout.');
        } finally {
            setIsLoading(false);
        }
    };

    /* ─── EMPTY CART ─────────────────────────────────── */
    if (cart.length === 0 && messageType !== 'success') {
        return (
            <section className="min-h-[60vh] flex flex-col items-center justify-center p-8">
                <div className="text-8xl mb-6">🛒</div>
                <h2 className="text-2xl font-bold text-slate-700 mb-2">Your cart is empty</h2>
                <p className="text-slate-400 mb-8">Add medicines and health products to get started.</p>
                <button onClick={() => showPage('products')}
                    className="bg-sky-500 hover:bg-sky-600 text-white font-bold py-3 px-8 rounded-full transition-all shadow-lg shadow-sky-500/20">
                    Browse Products
                </button>
            </section>
        );
    }

    /* ─── MAIN CART VIEW ─────────────────────────────── */
    return (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-sky-500 rounded-xl flex items-center justify-center shadow-md shadow-sky-500/20">
                        <i className="fas fa-shopping-cart text-white text-lg"></i>
                    </div>
                    <div>
                        <h1 className="text-2xl font-extrabold text-slate-800">Shopping Cart</h1>
                        <p className="text-sm text-slate-400">{cart.length} item{cart.length !== 1 ? 's' : ''} in your cart</p>
                    </div>
                </div>
                <button
                    onClick={() => showPage('products')}
                    className="flex items-center gap-2 px-5 py-2.5 bg-sky-50 hover:bg-sky-100 text-sky-600 hover:text-sky-700 rounded-xl font-bold text-sm transition-all border border-sky-200/50 shadow-sm"
                >
                    <i className="fas fa-plus"></i> Add More Items
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* ── LEFT: Cart Items + Delivery ── */}
                <div className="lg:col-span-2 space-y-4">
                    {/* Cart Items */}
                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                        <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
                            <h2 className="font-bold text-slate-700">Items</h2>
                            <div className="flex items-center gap-3">
                                <button onClick={() => showPage('products')} className="text-xs text-sky-500 hover:text-sky-700 font-bold transition-colors">
                                    + Add More Items
                                </button>
                                <span className="text-slate-200 text-xs">|</span>
                                <button onClick={() => setCart([])} className="text-xs text-red-400 hover:text-red-600 font-medium transition-colors">
                                    Clear all
                                </button>
                            </div>
                        </div>
                        <div className="p-4 space-y-3">
                            {cart.map(item => (
                                <CartItem key={item.productId} item={item} updateQuantity={updateCartQuantity} removeFromCart={removeFromCart} />
                            ))}
                        </div>
                    </div>

                    {/* Wallet Usage */}
                    {isAuthenticated && currentUserWalletBalance > 0 && (
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                            <h2 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                                <i className="fas fa-wallet text-sky-400"></i> Wallet Balance: ₹{currentUserWalletBalance.toFixed(2)}
                            </h2>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    checked={useWallet} 
                                    onChange={(e) => setUseWallet(e.target.checked)}
                                    className="w-4 h-4 text-sky-500 rounded focus:ring-sky-400 border-slate-300"
                                />
                                <span className="text-sm font-semibold text-slate-600">Use wallet balance for this order</span>
                            </label>
                        </div>
                    )}

                    {/* Delivery Options */}
                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                        <h2 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                            <i className="fas fa-truck text-sky-400"></i> Delivery Method
                        </h2>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            {['Home Delivery', 'Store Pickup'].map(method => (
                                <label key={method}
                                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${deliveryMethod === method ? 'border-sky-400 bg-sky-50' : 'border-slate-200 hover:border-slate-300'}`}>
                                    <input type="radio" name="deliveryMethod" value={method}
                                        checked={deliveryMethod === method}
                                        onChange={e => setDeliveryMethod(e.target.value)}
                                        className="hidden" />
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${deliveryMethod === method ? 'border-sky-500 bg-sky-500' : 'border-slate-300'}`}>
                                        {deliveryMethod === method && <div className="w-2 h-2 rounded-full bg-white" />}
                                    </div>
                                    <div>
                                        <p className="font-semibold text-slate-700 text-sm">{method}</p>
                                        <p className="text-xs text-slate-400">{method === 'Home Delivery' ? 'Delivered to your door' : 'Pick up at our store'}</p>
                                    </div>
                                </label>
                            ))}
                        </div>

                        {/* Address Form */}
                        {deliveryMethod === 'Home Delivery' && (
                            <div className="space-y-3">
                                {currentUserAddresses && currentUserAddresses.length > 0 && (
                                    <div className="mb-4">
                                        <label className="text-sm font-bold text-slate-700 mb-2 block flex items-center gap-2">
                                            <i className="fas fa-address-book text-sky-500"></i> Select a saved address
                                        </label>
                                        <div className="flex flex-col gap-2">
                                            {currentUserAddresses.map((addr, idx) => {
                                                const isSelected = address && address.line1 === addr.line1 && address.name === addr.name;
                                                return (
                                                    <button
                                                        key={idx}
                                                        onClick={() => setAddress(addr)}
                                                        className={`text-left border rounded-xl p-3 flex items-center gap-3 transition-all focus:outline-none focus:ring-2 focus:ring-sky-500 ${isSelected ? 'border-sky-400 bg-sky-50' : 'border-slate-200 hover:border-slate-300'}`}
                                                    >
                                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-sky-500 bg-sky-500' : 'border-slate-300'}`}>
                                                            {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="font-bold text-slate-700 text-sm">
                                                                <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full mr-2">{addr.label}</span>
                                                                {addr.name || 'No Name'}
                                                            </p>
                                                            <p className="text-xs text-slate-500 mt-1 line-clamp-1">{addr.line1}, {addr.city}</p>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                            {(() => {
                                                const isFormCleared = !address.line1 && !address.name && !address.phone;
                                                return (
                                                    <button 
                                                        onClick={() => setAddress({ name: '', phone: '', line1: '', line2: '', pincode: '', city: '', state: '' })}
                                                        className={`text-left border border-dashed rounded-xl p-3 flex items-center gap-3 transition-all focus:outline-none focus:ring-2 focus:ring-sky-500 ${isFormCleared ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 hover:border-slate-300 text-sky-600 hover:text-sky-800'}`}
                                                    >
                                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isFormCleared ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>
                                                            {isFormCleared && <div className="w-2 h-2 rounded-full bg-white" />}
                                                        </div>
                                                        <span className="text-sm font-bold">
                                                            + Enter a new address below
                                                        </span>
                                                    </button>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                )}
                                
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-semibold text-slate-500 mb-1 block">Patient's Full Name *</label>
                                        <input type="text" placeholder="Recipient name"
                                            value={address.name} onChange={e => setAddress(p => ({ ...p, name: e.target.value }))}
                                            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-slate-500 mb-1 block">Mobile Number *</label>
                                        <input type="tel" placeholder="10-digit mobile"
                                            value={address.phone} onChange={e => setAddress(p => ({ ...p, phone: e.target.value }))}
                                            maxLength={10}
                                            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                    {/* Referred By */}
                                    <div className="border border-violet-100 bg-violet-50 rounded-xl p-3">
                                        <label className="text-xs font-bold text-violet-700 mb-1.5 flex items-center gap-1.5 block">
                                            <i className="fas fa-user-tag"></i> Referred By <span className="text-violet-400 font-normal">(optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Doctor or person who referred..."
                                            value={referredBy}
                                            onChange={e => setReferredBy(e.target.value)}
                                            className="w-full border border-violet-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-400 text-violet-800 font-medium"
                                        />
                                    </div>

                                    {/* Patient Age & Gender */}
                                    <div className="border border-rose-100 bg-rose-50 rounded-xl p-3">
                                        <label className="text-xs font-bold text-rose-700 mb-2 flex items-center gap-1.5 block">
                                            <i className="fas fa-user"></i> Patient Info <span className="text-rose-400 font-normal">(optional)</span>
                                        </label>
                                        <div className="flex gap-2">
                                            <input
                                                type="number"
                                                min="1" max="120"
                                                placeholder="Age"
                                                value={patientAge}
                                                onChange={e => setPatientAge(e.target.value)}
                                                className="w-20 border border-rose-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-rose-300 font-bold text-rose-800 text-center"
                                            />
                                            <select
                                                value={patientGender}
                                                onChange={e => setPatientGender(e.target.value)}
                                                className="flex-1 border border-rose-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-rose-300 font-semibold text-rose-800 cursor-pointer"
                                            >
                                                <option value="">Gender</option>
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-3">
                                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Address Line 1 *</label>
                                    <input type="text" placeholder="House / Flat no., Street, Area"
                                        value={address.line1} onChange={e => setAddress(p => ({ ...p, line1: e.target.value }))}
                                        className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent" />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Address Line 2 <span className="text-slate-400 font-normal">(optional)</span></label>
                                    <input type="text" placeholder="Landmark, Colony"
                                        value={address.line2} onChange={e => setAddress(p => ({ ...p, line2: e.target.value }))}
                                        className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent" />
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <label className="text-xs font-semibold text-slate-500 mb-1 block">Pincode *</label>
                                        <input type="text" placeholder="6-digit"
                                            value={address.pincode} onChange={e => setAddress(p => ({ ...p, pincode: e.target.value }))}
                                            maxLength={6}
                                            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-slate-500 mb-1 block">City *</label>
                                        <input type="text" placeholder="City"
                                            value={address.city} onChange={e => setAddress(p => ({ ...p, city: e.target.value }))}
                                            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-slate-500 mb-1 block">State *</label>
                                        <select value={address.state} onChange={e => setAddress(p => ({ ...p, state: e.target.value }))}
                                            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent bg-white">
                                            <option value="">Select</option>
                                            {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                        {deliveryMethod === 'Store Pickup' && (
                            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 flex items-start gap-3">
                                <i className="fas fa-map-marker-alt text-sky-500 mt-0.5"></i>
                                <div>
                                    <p className="font-semibold text-sky-700 text-sm">QwickMed Store</p>
                                    <p className="text-xs text-sky-600">Ready for pickup in 30–60 mins after order confirmation.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── RIGHT: Order Summary ── */}
                <div className="space-y-4">
                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 sticky top-24">
                        <h2 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                            <i className="fas fa-receipt text-sky-400"></i> Order Summary
                        </h2>

                        {/* Itemised list */}
                        <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
                            {cart.map(item => (
                                <div key={item.productId} className="flex justify-between items-start text-sm">
                                    <div className="flex-1 pr-2 min-w-0">
                                        <p className="text-slate-600 line-clamp-1">{item.name} <span className="text-slate-400 font-semibold">×{item.quantity}</span></p>
                                        {item.pack && <p className="text-[10px] text-slate-400 font-semibold mt-0.5">({item.pack})</p>}
                                    </div>
                                    <span className="font-semibold text-slate-800 flex-shrink-0">₹{(item.price * item.quantity).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-dashed border-slate-200 pt-3 space-y-2">
                            <div className="flex justify-between text-sm text-slate-600">
                                <span>Subtotal</span>
                                <span className="font-semibold">₹{cartTotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm text-slate-600">
                                <span>Delivery</span>
                                <span className="text-green-600 font-semibold">FREE</span>
                            </div>

                            {walletAmountUsed > 0 && (
                                <div className="flex justify-between text-sm text-green-600">
                                    <span>Wallet Applied</span>
                                    <span className="font-semibold">−₹{walletAmountUsed.toFixed(2)}</span>
                                </div>
                            )}
                        </div>

                        <div className="border-t border-slate-200 mt-3 pt-3 flex justify-between items-center">
                            <span className="font-extrabold text-slate-800">Total</span>
                            <span className="font-extrabold text-sky-600 text-xl">₹{finalTotal.toFixed(2)}</span>
                        </div>

                        <p className="text-[10px] text-slate-400 mt-2 italic text-center">* Prices may vary. Final amount will reflect in your bill.</p>

                        {/* Payment method badge - COD Only */}
                        <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center gap-3">
                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="fas fa-money-bill-wave text-green-600 text-sm"></i>
                            </div>
                            <div>
                                <p className="font-bold text-slate-700 text-xs">Cash on Delivery</p>
                                <p className="text-[10px] text-slate-400">Pay when your order arrives</p>
                            </div>
                        </div>

                        {/* Checkout Button */}
                        <button
                            onClick={handleCheckout}
                            disabled={isLoading || cart.length === 0}
                            className="mt-4 w-full bg-sky-500 hover:bg-sky-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-extrabold py-3.5 rounded-2xl transition-all shadow-lg shadow-sky-500/20 hover:shadow-sky-500/30 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2"
                        >
                            {isLoading ? (
                                <><i className="fas fa-spinner fa-spin"></i> Placing Order...</>
                            ) : (
                                <><i className="fas fa-lock text-sm"></i> Place Order · ₹{finalTotal.toFixed(2)}</>
                            )}
                        </button>

                        {/* Message */}
                        {checkoutMessage && (
                            <div className={`mt-3 text-center text-xs font-semibold rounded-xl p-2 ${messageType === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                                {checkoutMessage}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CartView;
